import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { PursuitPPContent } from '../../../../../../gen/ts/mono/datagateway/v1/pursuit_pp_pb';
import { projectDataClient } from '../../../lib/connect/clients';
import { pastPerformanceClient } from '../../Library/ppClient';
import { libraryReleaseClient } from '../../Library/reuseClient';
import { SourceChangeNotice } from '../../Library/SourceChangeNotice';
import { ReadOnlyScope } from '../record/ReadOnlyScope';
import { pursuitPPClient } from './api';
import { CreateReference } from './CreateReference';
import { Choice, Facts } from './Fields';
import { TailoringEditor } from './TailoringEditor';
export function PastPerformanceTab({ captureProjectId, readOnly = false }) {
    const [candidate, setCandidate] = useState('');
    const [created, setCreated] = useState([]);
    const [active, setActive] = useState('');
    const [evidence, setEvidence] = useState();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [limit, setLimit] = useState(50);
    const workspace = useQuery({
        queryKey: [
            'capture',
            captureProjectId,
            'pp-context'
        ],
        queryFn: ()=>projectDataClient.getCaptureWorkspace({
                captureProjectId
            })
    });
    const references = useQuery({
        queryKey: [
            'pursuit-pp-references',
            limit
        ],
        queryFn: async ()=>(await pastPerformanceClient.listPastPerformanceReferences({
                limit
            })).references
    });
    const releases = useQuery({
        queryKey: [
            'pursuit-pp-releases'
        ],
        queryFn: async ()=>(await libraryReleaseClient.listSelectableReleases({
                itemType: 'reference'
            })).releases
    });
    const selections = useQuery({
        queryKey: [
            'pursuit-pp',
            captureProjectId
        ],
        queryFn: async ()=>(await pursuitPPClient.listPursuitPP({
                captureProjectId
            })).selections,
        refetchInterval: 30000,
        retry: false
    });
    const allReferences = [
        ...new Map([
            ...references.data ?? [],
            ...created
        ].filter((r)=>r.item).map((r)=>[
                r.item?.id,
                r
            ])).values()
    ];
    const candidates = [
        ...allReferences.filter((r)=>!(releases.data ?? []).some((release)=>release.libraryItemId === r.item?.id)).map((r)=>({
                id: r.item?.id ?? '',
                title: `${r.item?.title} — ${r.details?.referenceOrigin ?? 'manual'} / ${r.item?.status}`
            })),
        ...(releases.data ?? []).map((release)=>({
                id: release.libraryItemId,
                title: `${release.releasedTitle} — shared v${release.version}`,
                release
            }))
    ];
    const selected = selections.data?.find((s)=>s.id === active);
    const chosen = candidates.find((c)=>c.id === candidate);
    const context = {
        scope: workspace.data?.summary?.opportunityHypothesis || 'Scope not recorded',
        customer: workspace.data?.profile?.customerAgencyNames || workspace.data?.summary?.customerOrgId || 'Customer not recorded',
        scale: [
            workspace.data?.profile?.totalProgramValue,
            workspace.data?.profile?.periodOfPerformance
        ].filter(Boolean).join(' / ') || 'Scale / period not recorded',
        capabilities: workspace.data?.workstreamItems.filter((item)=>item.workstreamType === 4).map((item)=>`${item.title}: ${item.body}`).join('\n') || 'No capability workstream evidence recorded',
        needs: workspace.data?.tasks.map((task)=>task.title).join('\n') || 'No needs / tasks recorded'
    };
    async function readEvidence(id, released) {
        setBusy(true);
        setError('');
        setEvidence(undefined);
        try {
            if (released) setEvidence({
                release: await libraryReleaseClient.getReleasedDerivative({
                    libraryItemId: id
                })
            });
            else setEvidence({
                reference: (await pastPerformanceClient.getPastPerformanceReference({
                    libraryItemId: id
                })).reference
            });
        } catch  {
            setError('Reference evidence is unavailable or not permitted. No Program access is required; check reference permissions or retry.');
        } finally{
            setBusy(false);
        }
    }
    async function reload() {
        setError('');
        setEvidence(undefined);
        return selections.refetch();
    }
    const denied = selections.error instanceof ConnectError && [
        Code.PermissionDenied,
        Code.NotFound,
        Code.Unauthenticated
    ].includes(selections.error.code);
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            "data-testid": Testids.pursuitPP.root,
            children: [
                /*#__PURE__*/ _jsx("h2", {
                    className: "text-lg font-semibold",
                    children: "Past Performance"
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm",
                    children: "You select relevant experience. Selection is a manual assessment, not an eligibility or external-use approval. Reference access grants no Program access."
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    variant: "secondary",
                    "data-testid": Testids.pursuitPP.reload,
                    disabled: busy || selections.isFetching,
                    onClick: ()=>{
                        void reload();
                        void references.refetch();
                        void releases.refetch();
                    },
                    children: "Refresh current access and references"
                }),
                (busy || selections.isLoading || references.isLoading || releases.isLoading) && /*#__PURE__*/ _jsx("output", {
                    children: "Loading Past Performance…"
                }),
                (error || selections.isError || references.isError || releases.isError) && /*#__PURE__*/ _jsx("p", {
                    role: "alert",
                    "data-testid": Testids.pursuitPP.error,
                    children: error || (denied ? 'Past Performance is not accessible for this Pursuit.' : 'Some Past Performance data could not be loaded. Retry with Refresh; drafts are retained.')
                }),
                !denied && !selections.isLoading && /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "grid items-start gap-4 lg:grid-cols-3",
                            children: [
                                /*#__PURE__*/ _jsx(Facts, {
                                    title: "Known Pursuit context",
                                    children: workspace.isLoading ? 'Loading Pursuit context…' : workspace.isError ? /*#__PURE__*/ _jsxs(_Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsx("p", {
                                                children: "Pursuit context unavailable."
                                            }),
                                            /*#__PURE__*/ _jsx(SpecraButton, {
                                                type: "button",
                                                variant: "secondary",
                                                onClick: ()=>void workspace.refetch(),
                                                children: "Retry context"
                                            })
                                        ]
                                    }) : Object.entries(context).map(([key, value])=>/*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                /*#__PURE__*/ _jsxs("strong", {
                                                    children: [
                                                        key,
                                                        ":"
                                                    ]
                                                }),
                                                " ",
                                                value
                                            ]
                                        }, key))
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-3 lg:col-span-2",
                                    children: [
                                        /*#__PURE__*/ _jsx(Choice, {
                                            label: "Permissioned PP references",
                                            testId: Testids.pursuitPP.candidate,
                                            value: candidate,
                                            onChange: (id)=>{
                                                setCandidate(id);
                                                setEvidence(undefined);
                                            },
                                            options: [
                                                {
                                                    value: '',
                                                    label: 'Choose a reference to review'
                                                },
                                                ...candidates.map((c)=>({
                                                        value: c.id,
                                                        label: c.title
                                                    }))
                                            ]
                                        }),
                                        !candidates.length && !references.isLoading && /*#__PURE__*/ _jsx("p", {
                                            children: "No permitted references found. Add an incomplete manual reference below."
                                        }),
                                        references.data && references.data.length >= limit && /*#__PURE__*/ _jsx(SpecraButton, {
                                            variant: "secondary",
                                            onClick: ()=>setLimit(limit + 50),
                                            children: "Load more references"
                                        }),
                                        chosen && /*#__PURE__*/ _jsxs("div", {
                                            className: "flex gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    variant: "secondary",
                                                    "data-testid": Testids.pursuitPP.reference,
                                                    disabled: busy,
                                                    onClick: ()=>void readEvidence(chosen.id, !!chosen.release),
                                                    children: "View Reference / evidence"
                                                }),
                                                /*#__PURE__*/ _jsx(SpecraButton, {
                                                    "data-testid": Testids.pursuitPP.select,
                                                    disabled: busy || !releases.isSuccess,
                                                    onClick: async ()=>{
                                                        if (active && !window.confirm('Select another reference? Unsaved edits will be discarded.')) return;
                                                        setBusy(true);
                                                        setError('');
                                                        try {
                                                            const result = await pursuitPPClient.selectPursuitPP({
                                                                captureProjectId,
                                                                libraryItemId: chosen.id,
                                                                releaseId: chosen.release?.releaseId ?? '',
                                                                content: new PursuitPPContent()
                                                            });
                                                            if (!result.selection) throw new Error('No selection');
                                                            await selections.refetch();
                                                            setActive(result.selection.id);
                                                        } catch  {
                                                            setError('Reference could not be selected. Only your own drafts or insights shared with you can be selected. Refresh and retry.');
                                                        } finally{
                                                            setBusy(false);
                                                        }
                                                    },
                                                    children: "Select for this Pursuit"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(CreateReference, {
                                            onCreated: (reference)=>{
                                                setCreated([
                                                    ...created,
                                                    reference
                                                ]);
                                                setCandidate(reference.item?.id ?? '');
                                                setEvidence({
                                                    reference
                                                });
                                            }
                                        })
                                    ]
                                })
                            ]
                        }),
                        evidence && /*#__PURE__*/ _jsx(Facts, {
                            title: "Current source reference — permissioned read only",
                            children: /*#__PURE__*/ _jsx("div", {
                                "data-testid": Testids.pursuitPP.sourceEvidence,
                                children: evidence.release ? evidence.release.withdrawn ? 'Source withdrawn — content redacted' : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                evidence.release.releasedTitle,
                                                " — v",
                                                evidence.release.version
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            children: evidence.release.releasedContent
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Restrictions: ",
                                                evidence.release.restrictions || 'None recorded'
                                            ]
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("p", {
                                            children: evidence.reference?.item?.title
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Source status: ",
                                                evidence.reference?.item?.status || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Library ID: ",
                                                evidence.reference?.item?.id
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Origin: ",
                                                evidence.reference?.details?.referenceOrigin,
                                                "; provider:",
                                                ' ',
                                                evidence.reference?.item?.providerCompanyProfileId || 'Not recorded',
                                                "; role: ",
                                                evidence.reference?.details?.providerRole || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Customer: ",
                                                evidence.reference?.item?.customerAgencyName || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Scope: ",
                                                evidence.reference?.item?.scopeContext || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Capabilities: ",
                                                evidence.reference?.item?.capabilityContext || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            children: evidence.reference?.item?.summary
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            children: evidence.reference?.item?.preparedContent
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Basis: ",
                                                evidence.reference?.details?.basisKind,
                                                "; source:",
                                                ' ',
                                                evidence.reference?.details?.sourcePerson || 'Not recorded'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Outcome: ",
                                                evidence.reference?.details?.outcome || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Limitations: ",
                                                evidence.reference?.details?.limitations || 'Not recorded'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Contact: ",
                                                evidence.reference?.details?.referenceContact || 'Unknown'
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("p", {
                                            children: [
                                                "Contract: ",
                                                evidence.reference?.details?.contractIdentifier || 'Unknown'
                                            ]
                                        })
                                    ]
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx("h3", {
                            className: "font-semibold",
                            children: "Selected experience"
                        }),
                        !selections.data?.length && /*#__PURE__*/ _jsx("p", {
                            children: "No references selected. Review a reference or add one manually, then select it."
                        }),
                        /*#__PURE__*/ _jsx(Choice, {
                            label: "Selected reference to tailor",
                            testId: Testids.pursuitPP.selectedReference,
                            value: active,
                            onChange: (id)=>{
                                if (!active || window.confirm('Switch reference? Unsaved edits will be discarded.')) {
                                    setActive(id);
                                    setEvidence(undefined);
                                }
                            },
                            options: [
                                {
                                    value: '',
                                    label: 'Choose selected experience'
                                },
                                ...(selections.data ?? []).map((s)=>({
                                        value: s.id,
                                        label: `${s.libraryItemId} — local revision ${s.revision} / ${s.reuseStatus}`
                                    }))
                            ]
                        }),
                        selected && (selected.sourceWithdrawn ? /*#__PURE__*/ _jsx(Facts, {
                            title: "Source withdrawn",
                            children: "Content redacted. Select a permitted replacement or contact the source owner; this record cannot be edited."
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    disabled: busy,
                                    "data-testid": Testids.pursuitPP.originalReference,
                                    onClick: ()=>void readEvidence(selected.libraryItemId, !!selected.releaseId),
                                    children: "View original Reference / evidence"
                                }),
                                selected.reuseId && /*#__PURE__*/ _jsx(SourceChangeNotice, {
                                    reuseId: selected.reuseId
                                }),
                                /*#__PURE__*/ _jsx(TailoringEditor, {
                                    selection: selected,
                                    context: context,
                                    onReload: reload,
                                    onSaved: ()=>void selections.refetch()
                                }, selected.id)
                            ]
                        }))
                    ]
                })
            ]
        })
    });
}
